import type { PersonOrgChart } from "@mirage/service-people/types";
import { getOrgChart } from "@mirage/service-typeahead-search";
import { useEffect, useState } from "react";

// Maps uuid/accountId/email/displayHandle to PersonOrgChart object for faster lookup
const personOrgChartCache = new Map<string, PersonOrgChart | null>();

export function useOrgChart(): PersonOrgChart[] | undefined {
  // Returns:
  // - `PersonOrgChart[]` representing the org chart if it has been fetched
  // - `undefined` if the org chart is still being fetched
  const [orgChart, setOrgChart] = useState<PersonOrgChart[] | undefined>(
    undefined,
  );

  useEffect(() => {
    const fetchOrgChart = async () => {
      const orgChart = await getOrgChart();
      setOrgChart(orgChart);
    };
    fetchOrgChart();
  }, []);

  return orgChart;
}

export function usePersonFromOrgChart(
  uuid: string,
): PersonOrgChart | null | undefined {
  // Returns:
  // - PersonOrgChart if the person with the given uuid exists in the org chart
  // - `null` if the person with the given uuid does not exist in the org chart
  // - `undefined` if the org chart is still being fetched
  const orgChart = useOrgChart();
  if (!uuid) return null;
  if (!orgChart) return undefined;

  if (personOrgChartCache.has(uuid)) {
    return personOrgChartCache.get(uuid);
  }

  const result = orgChart.find((p) => p.uuid === uuid) || null;
  personOrgChartCache.set(uuid, result);
  return result;
}

export function usePersonFromOrgChartByAccountId(
  accountId: string | undefined,
): PersonOrgChart | null | undefined {
  // Returns:
  // - PersonOrgChart if the person with the given account ID exists in the org chart
  // - `null` if there is not exactly 1 exact match (i.e. 0 or 2+ matches)
  // - `undefined` if the org chart is still being fetched
  const orgChart = useOrgChart();
  if (!accountId) return null;
  if (!orgChart) return undefined;

  if (personOrgChartCache.has(accountId)) {
    return personOrgChartCache.get(accountId);
  }

  const matches = orgChart.filter((p) => p.accountId == accountId);
  const result = matches.length === 1 ? matches[0] : null;
  personOrgChartCache.set(accountId, result);
  return result;
}

export function usePersonFromOrgChartByDisplayHandle(
  displayHandle: string,
): PersonOrgChart | null | undefined {
  // Returns:
  // - PersonOrgChart if the person with the given display handle exists in the org chart
  // - `null` if there is not exactly 1 exact match (i.e. 0 or 2+ matches)
  // - `undefined` if the org chart is still being fetched
  const orgChart = useOrgChart();
  if (!displayHandle) return null;
  if (!orgChart) return undefined;

  if (personOrgChartCache.has(displayHandle)) {
    return personOrgChartCache.get(displayHandle);
  }

  const matches = orgChart.filter(
    (p) => p.displayHandle?.toLowerCase() === displayHandle.toLowerCase(),
  );
  const result = matches.length === 1 ? matches[0] : null;
  personOrgChartCache.set(displayHandle, result);
  return result;
}

export function usePersonFromOrgChartByEmail(
  email: string | undefined,
): PersonOrgChart | null | undefined {
  // Returns:
  // - PersonOrgChart if the person with the given email exists in the org chart
  // - `null` if there is not exactly 1 exact match (i.e. 0 or 2+ matches)
  // - `undefined` if the org chart is still being fetched
  const orgChart = useOrgChart();
  if (!email) return null;
  if (!orgChart) return undefined;

  if (personOrgChartCache.has(email)) {
    return personOrgChartCache.get(email);
  }

  const matches = orgChart.filter((p) => p.email == email);
  const result = matches.length === 1 ? matches[0] : null;
  personOrgChartCache.set(email, result);
  return result;
}

export function usePersonFromOrgChartSearch(
  query: string,
  matchStartsWith: boolean = false,
): PersonOrgChart | null | undefined {
  // By default, it matches people with the given display handle, email, or name exactly (string equality).
  // If `matchStartsWith` is true, it matches people whose display handle, email, or name starts with the given query.
  // For display names, it matches any part (separated by a space) of the name that starts with the query.
  // Returns:
  // - PersonOrgChart if the person with the given display handle, email, or name
  //   exists in the org chart
  // - `null` if there is not exactly 1 exact match (i.e. 0 or 2+ matches)
  // - `undefined` if the org chart is still being fetched
  const orgChart = useOrgChart();
  if (!orgChart) return undefined;

  const lowerQuery = query.toLowerCase();
  const matches = orgChart.filter((p) => {
    if (matchStartsWith) {
      const nameParts = p.displayName?.toLowerCase().split(" ") || [];
      for (const part of nameParts) {
        if (part.startsWith(lowerQuery)) return true;
      }
      return (
        p.displayHandle?.toLowerCase().startsWith(lowerQuery) ||
        p.displayName?.toLowerCase().startsWith(lowerQuery) ||
        p.email.toLowerCase().startsWith(lowerQuery)
      );
    } else {
      return (
        p.displayHandle?.toLowerCase() === lowerQuery ||
        p.displayName?.toLowerCase() === lowerQuery ||
        p.email.toLowerCase() === lowerQuery
      );
    }
  });
  if (matches.length === 1) {
    return matches[0];
  }
  return null;
}
